import * as Api from '@ViewModels';
import { getShortenedString } from './stringUtils';

/**
 * Extracts plain text content from raw rich content for social media
 */
export const getSocialMediaPlainTextContentFromRawRichContent = (rawRichContent: Api.IRawRichTextContentState) => {
	const htmlStringValue = Api.getContentHtmlStringValueFromRawRichTextContentState(rawRichContent);
	const el = document.createElement('div');
	el.innerHTML = htmlStringValue;

	let result = '';
	if (el.firstElementChild) {
		for (let i = 0; i < el.children.length; i++) {
			const child = el.children.item(i);
			const text = child.textContent;
			result =
				result + (!text || text.toLocaleLowerCase() === ';nbsp' ? '' : text) + (i + 1 < el.children.length ? '\n' : '');
		}
	} else if (!el.textContent || el.textContent?.toLocaleLowerCase() === ';nbsp') {
		result = '';
	} else {
		result = el.textContent;
	}

	if (el.parentNode) {
		el.parentNode.removeChild(el);
	}
	return result;
};

/**
 * Extracts the greeting from a template card
 */
export const getTemplateCardGreeting = (cardContent: Api.IRawRichTextContentState) => {
	/** Get document content from content.document */
	const _doc = new DOMParser()?.parseFromString(cardContent?.document, 'text/html')?.body;
	/** Get lev-content element from document */
	const _levContentElement = Array.from(_doc?.getElementsByTagName('lev-content'))?.[0] || null;
	/** Get placeholder element from lev-content element */
	const _placeholder =
		(_levContentElement && _levContentElement?.querySelectorAll('span[data-placeholder]')?.[0]) || null;
	/**
	 * @returns Object of empty strings
	 * @If placeholder does not exist,
	 */
	if (!_placeholder) {
		return {
			greeting: '',
			greetingString: '',
			placeholder: null,
		};
	}
	/** Get first element from lev-content element */
	const _firstElementContent = new DOMParser()?.parseFromString(
		_levContentElement?.firstElementChild?.outerHTML,
		'text/html'
	);
	/** Get first element child from the first element content */
	const firstElementFirstChild =
		_firstElementContent && _firstElementContent?.getElementsByTagName('p')?.[0]?.firstElementChild;
	/**
	 * @If the lev-content element's first element child is the same as the placeholder
	 * - get the first word from the lev-content element's innerHTML
	 * @ElSEIF the first element child exists (a <P> tag) and the first element child's innerHTML is not the same as the placeholder's innerHTML
	 * - get the first word from the first element child's innerHTML
	 * @ELSE get the first word from the first <p> tag element's innerHTML
	 */
	const firstWord =
		_levContentElement.firstElementChild === _placeholder
			? _levContentElement?.innerHTML?.split(' ')?.[0]
			: firstElementFirstChild && firstElementFirstChild?.innerHTML !== _placeholder?.innerHTML
				? firstElementFirstChild?.innerHTML?.split(' ')?.[0]
				: _firstElementContent?.getElementsByTagName('p')?.[0]?.innerHTML?.split(' ')?.[0] || '';
	/** Greeting is comprised of the first word and the placeholder element */
	const _greeting = (Boolean(firstWord) && firstWord + ' ' + _placeholder?.outerHTML) || '';
	/**
	 * Greeting string is used to compare the greeting to the template's content so that it can be removed from the
	 * template's content if it is the same as the greeting.
	 */
	const _greetingString = Boolean(firstWord) && firstWord + ' ' + _placeholder?.innerHTML;
	return {
		greeting: _greeting,
		greetingString: _greetingString,
		placeholder: _placeholder,
	};
};

/**
 * Gets plain text preview from raw rich text content with optional truncation
 */
export const getPlainTextPreviewForRawRichTextContent = (
	content: Api.IRawRichTextContentState,
	options?: {
		maxCharCount?: number;
		maxLineCount?: number;
		startingLineOffset?: number | ((rootElement?: Element, lines?: HTMLParagraphElement[]) => number);
	}
) => {
	let text = '';
	let truncated = false;
	try {
		const doc = new DOMParser().parseFromString(content?.document, 'text/html');
		const levContentElement = Array.from(doc.getElementsByTagName('lev-content'))?.[0];
		const lineElements = levContentElement ? Array.from(levContentElement.getElementsByTagName('p')) : [];

		if (lineElements?.length === 0) {
			text = levContentElement.textContent;
			return {
				text: getShortenedString(text.trim(), options.maxCharCount),
				truncated: text?.length > options.maxCharCount ? true : false,
			};
		}

		let lineIndex = 0;
		if (options?.startingLineOffset !== undefined && options?.startingLineOffset !== null) {
			lineIndex =
				typeof options.startingLineOffset === 'function'
					? options.startingLineOffset(levContentElement, lineElements)
					: options.startingLineOffset;
		}
		const startingLineIndex = lineIndex;

		if (!lineElements?.[lineIndex]?.textContent) {
			if (levContentElement?.textContent) {
				text = text.concat(levContentElement.textContent);
			}
		} else {
			do {
				text = text.concat(lineElements?.[lineIndex]?.textContent);
				text = text.concat(' ');
				lineIndex++;
				if (
					(options?.maxCharCount > -1 && text.length >= options.maxCharCount) ||
					(options?.maxLineCount > -1 && lineIndex - startingLineIndex >= options.maxLineCount)
				) {
					truncated = true;
					break;
				}
			} while (lineIndex < lineElements.length);
		}
	} catch {
		console.log('DOMParser error');
	}
	const truncate = options?.maxCharCount > -1 && text.length > options.maxCharCount;
	if (truncate) {
		truncated = true;
	}
	return {
		text: truncate ? getShortenedString(text.trim(), options.maxCharCount) : text.trim(),
		truncated,
	};
};
